import { useCallback, useMemo, useState } from 'react'
import { X, Sparkles, BarChart3 } from 'lucide-react'
import { AISummary } from '@/components/profile/AISummary'
import { AIChatPage } from '@/components/ai/AIChatPage'
import { ChatPanel } from '@/components/groups/ChatPanel'
import { useTaskStore } from '@/stores/taskStore'
import { useUIStore } from '@/stores/uiStore'
import { useSharedGroupStore } from '@/stores/sharedGroupStore'
import { useAuthStore } from '@/stores/authStore'

export function AuxPanel() {
  const tasks = useTaskStore((s) => s.tasks)
  const toggleAuxPanel = useUIStore((s) => s.toggleAuxPanel)
  const auxPanelGroupId = useUIStore((s) => s.auxPanelGroupId)

  // 기본 패널(비채팅) 보기 모드: AI 요약 ↔ AI 코치 (flows#6)
  const [auxView, setAuxView] = useState<'summary' | 'ai'>('summary')

  const uid = useAuthStore((s) => s.user?.uid)
  const chatMessages = useSharedGroupStore((s) => s.chatMessages)
  const olderChatMessages = useSharedGroupStore((s) => s._olderChatMessages)
  const pendingChatMessages = useSharedGroupStore((s) => s._pendingChatMessages)
  const activeGroupMembers = useSharedGroupStore((s) => s.activeGroupMembers)
  const activeGroupTasks = useSharedGroupStore((s) => s.activeGroupTasks)
  const hasMoreMessages = useSharedGroupStore((s) => s.hasMoreMessages)
  const sendChat = useSharedGroupStore((s) => s.sendChat)
  const deleteChat = useSharedGroupStore((s) => s.deleteChat)
  const addChatReaction = useSharedGroupStore((s) => s.addChatReaction)
  const removeChatReaction = useSharedGroupStore((s) => s.removeChatReaction)
  const uploadChatImage = useSharedGroupStore((s) => s.uploadChatImage)
  const uploadChatVoice = useSharedGroupStore((s) => s.uploadChatVoice)
  const uploadChatFile = useSharedGroupStore((s) => s.uploadChatFile)
  const updateChatReadPosition = useSharedGroupStore((s) => s.updateChatReadPosition)
  const loadMoreMessages = useSharedGroupStore((s) => s.loadMoreMessages)
  const updateTypingStatus = useSharedGroupStore((s) => s.updateTypingStatus)
  const toggleTask = useSharedGroupStore((s) => s.toggleTask)
  const addTask = useSharedGroupStore((s) => s.addTask)
  const sharedGroups = useSharedGroupStore((s) => s.sharedGroups)

  const groupName = auxPanelGroupId
    ? sharedGroups.find((g) => g.id === auxPanelGroupId)?.name || '채팅'
    : ''

  const allMessages = useMemo(() => {
    if (!auxPanelGroupId) return []
    const confirmedIds = new Set(chatMessages.map(m => m.id))
    const olderUnique = olderChatMessages.filter(m => !confirmedIds.has(m.id))
    const pendingUnique = pendingChatMessages.filter(m => !confirmedIds.has(m.id))
    return [...olderUnique, ...chatMessages, ...pendingUnique]
  }, [auxPanelGroupId, chatMessages, olderChatMessages, pendingChatMessages])

  const handleLoadMore = useCallback(
    () => auxPanelGroupId ? loadMoreMessages(auxPanelGroupId) : Promise.resolve(),
    [loadMoreMessages, auxPanelGroupId]
  )
  const handleUpdateReadPosition = useCallback(
    () => { if (auxPanelGroupId) updateChatReadPosition(auxPanelGroupId) },
    [updateChatReadPosition, auxPanelGroupId]
  )

  // 공유 그룹 채팅 모드
  if (auxPanelGroupId) {
    return (
      <div className="hidden lg:flex flex-col w-[400px] flex-shrink-0 rounded-xl border border-base overflow-hidden h-full bg-surface-primary shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-base flex-shrink-0">
          <h2 className="text-sm font-semibold text-heading truncate">{groupName}</h2>
          <button
            type="button"
            onClick={toggleAuxPanel}
            className="p-1 rounded-lg text-caption hover-surface transition-colors"
            aria-label="패널 닫기"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          <ChatPanel
            messages={allMessages}
            members={activeGroupMembers}
            tasks={activeGroupTasks}
            currentUid={uid}
            lastReadChatAt={activeGroupMembers.find((m) => m.uid === uid)?.lastReadChatAt}
            hasMoreMessages={hasMoreMessages}
            onSend={(payload) => sendChat(auxPanelGroupId, payload)}
            onDelete={(messageId) => deleteChat(auxPanelGroupId, messageId)}
            onReactionAdd={(messageId, emoji) => addChatReaction(auxPanelGroupId, messageId, emoji)}
            onReactionRemove={(messageId, emoji) => removeChatReaction(auxPanelGroupId, messageId, emoji)}
            onUploadImage={(file) => uploadChatImage(auxPanelGroupId, file)}
            onUploadVoice={(blob) => uploadChatVoice(auxPanelGroupId, blob)}
            onUploadFile={(file) => uploadChatFile(auxPanelGroupId, file)}
            onUpdateReadPosition={handleUpdateReadPosition}
            onLoadMore={handleLoadMore}
            onTyping={(isTyping) => updateTypingStatus(auxPanelGroupId, isTyping)}
            groupId={auxPanelGroupId}
            onToggleTask={(taskId, status) => toggleTask(auxPanelGroupId, taskId, status)}
            onAddTask={async (t) => { await addTask(auxPanelGroupId, { title: t.title, priority: (t.priority as 'none' | 'low' | 'medium' | 'high') || 'none', dueDate: t.dueDate }) }}
          />
        </div>
      </div>
    )
  }

  // 기본: AI 요약 ↔ AI 코치 (Sparkles 토글로 전환)
  const isAiMode = auxView === 'ai'
  return (
    <div className="hidden lg:flex flex-col w-[400px] flex-shrink-0 rounded-xl border border-base overflow-hidden h-full bg-surface-primary shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-base flex-shrink-0">
        <h2 className="text-sm font-semibold text-heading">
          {isAiMode ? 'AI 코치' : 'AI 요약'}
        </h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setAuxView(isAiMode ? 'summary' : 'ai')}
            className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg transition-colors ${
              isAiMode
                ? 'text-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'text-caption hover-surface'
            }`}
            aria-label={isAiMode ? 'AI 요약 보기' : 'AI 코치 열기'}
            aria-pressed={isAiMode}
            title={isAiMode ? 'AI 요약' : 'AI 코치'}
          >
            {isAiMode ? <BarChart3 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={toggleAuxPanel}
            className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-caption hover-surface transition-colors"
            aria-label="패널 닫기"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {isAiMode ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <AIChatPage embedded />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4">
          <AISummary tasks={tasks} />
        </div>
      )}
    </div>
  )
}
